import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import UxTemplateWizard from '../components/UxTemplateWizard'
import DeploymentTemplateWizard from '../components/DeploymentTemplateWizard'
import { toAssignmentGroup } from '../components/AssignmentBlade'
import {
  ComputerDesktopIcon, CloudArrowUpIcon,
  BoltIcon, XCircleIcon, ClockIcon, BellAlertIcon,
  PencilIcon, DocumentDuplicateIcon, TrashIcon,
} from '@heroicons/react/24/outline'
import { PageShell, PageHeader, Tabs, Breadcrumb, HowItWorks, Toggle, ConfirmDialog, EmptyState } from '../components/ui'
import { useAuthStore } from '../store/authStore'
import { uxSummary, intuneSummary, intuneAssignmentChips } from '../utils/templateSummary'
import type { IntentTone } from '../utils/templateSummary'
import {
  listDeploymentTemplates,
  listIntuneTemplates,
  listAzureADGroups,
  listAssignmentFilters,
  listIntuneAppCategories,
  updateDeploymentTemplate,
  updateIntuneTemplate,
  deleteDeploymentTemplate,
  deleteIntuneTemplate,
  duplicateDeploymentTemplate,
  duplicateIntuneTemplate,
  DeploymentTemplate,
  IntuneDeploymentTemplate,
  TemplateUsage,
} from '../services/api'
import { errorText } from '../utils/errorText'

/**
 * Settings -> Templates (016-template-model). Replaces the older Deployment Experience page.
 *
 * Two tabs, deliberately not three. Branding is NOT a template: it is the DEVICE-WIDE override at
 * %ProgramData%\sap\psadt\config (layer 3, local wins, every package on the device), with its own
 * signing story and its own lifecycle. Listing it here would say it is one kind of template among
 * three, and an admin would reasonably expect to attach it to a deployment the way they attach
 * these. It lives at Settings -> Branding instead.
 *
 * Per-template DEPLOYMENT-TIME identity (dialog style, accent, company name) does live here, on the
 * User Experience template's Appearance step. A branded device overrides it.
 *
 * The two objects are easy to confuse and the naming does not help:
 *   User Experience   = what the end user sees; baked onto the install command at publish.
 *   Intune Deployment = who gets the app and how; the Graph app object plus its assignments.
 * The design spec calls the second one "deployment template"; feature 013 calls the FIRST one
 * that. The header copy spells out the difference so an admin picking a tab is not guessing.
 */

type Tab = 'ux' | 'intune'

// uxSummary / intuneSummary now live in utils/templateSummary so the deploy wizard shows the
// SAME sentence for a template as this list does — recognising the template you picked is the
// entire job of its summary card.

/** One row action — the reference's `IconBtn` (its settings/templates/page.tsx:260). An icon
 *  with a `title`, which is both the tooltip and the accessible name: three text buttons per row
 *  are three words of chrome competing with the template's own name. */
const RowAction: React.FC<{
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  danger?: boolean
}> = ({ label, icon: Icon, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`rounded-md p-2 text-theme-text-tertiary transition-colors ${
      danger
        ? 'hover:bg-red-500/10 hover:text-red-500'
        : 'hover:bg-theme-tertiary hover:text-theme-text-primary'
    }`}
  >
    <Icon className="h-4 w-4" />
  </button>
)

const deploymentCount = (n: number) => `${n} deployment${n === 1 ? '' : 's'}`

/** "Used by 2 deployments: WinMerge · Default, WinMerge · QA". Nothing while the count is unknown. */
const UsedBy: React.FC<{ usage?: TemplateUsage; className?: string }> = ({
  usage,
  className = 'text-xs text-theme-text-tertiary',
}) => {
  if (!usage) return null
  if (usage.count === 0) return <p className={className}>Not used by any deployment</p>
  return (
    <p className={className} title={usage.deployments.join('\n')}>
      Used by {deploymentCount(usage.count)}: {usage.deployments.join(', ')}
    </p>
  )
}

/**
 * One template in a list.
 *
 * Actions are driven by what the API will actually accept. A read-only template gets Duplicate and
 * nothing else: the API answers 403 to an edit or a delete on one, so offering either taught the
 * admin that the portal lies to them, and offering neither (as the Intune tab did) left the row
 * with no way forward at all.
 */
const Row: React.FC<{
  name: string
  description?: string | null
  summary: string
  badges?: React.ReactNode
  onEdit?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  /** F-10b: the mock carries a per-row default switch (its templates page.tsx:204, used on both
   *  tabs). Ours only ever RENDERED `is_default` as a badge, so a tenant could see which template
   *  was default and had no way to change it. */
  defaultToggle?: React.ReactNode
  /** Keyed to what the template DOES, so the list is scannable before any text is read. */
  icon?: React.ReactNode
  /** Status chips shown under the name — intent and audience, which carry meaning colour can
   *  encode faster than a sentence can. */
  chips?: React.ReactNode
  /** The deployments that use the template. */
  usedBy?: TemplateUsage
}> = ({ name, description, summary, badges, icon, chips, usedBy, onEdit, onDuplicate, onDelete, defaultToggle }) => (
  <div className="flex items-center justify-between border border-theme-border-primary rounded-lg px-4 py-3">
    {icon && (
      <div className="mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-theme-tertiary text-theme-text-tertiary">
        {icon}
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-medium text-theme-text-primary">{name}</span>
        {badges}
      </div>
      {description && <p className="text-xs text-theme-text-tertiary mt-0.5 truncate">{description}</p>}
      {chips && <div className="mt-1 flex flex-wrap items-center gap-1">{chips}</div>}
      <p className="text-xs text-theme-text-secondary mt-1 truncate">{summary}</p>
      <UsedBy usage={usedBy} className="text-xs text-theme-text-tertiary mt-1 truncate" />
    </div>
    <div className="ml-3 flex shrink-0 items-center gap-3">
      {defaultToggle}
      <div className="flex items-center gap-1.5">
        {onEdit && <RowAction label="Edit" icon={PencilIcon} onClick={onEdit} />}
        {onDuplicate && (
          <RowAction label="Duplicate" icon={DocumentDuplicateIcon} onClick={onDuplicate} />
        )}
        {onDelete && <RowAction label="Delete" icon={TrashIcon} onClick={onDelete} danger />}
      </div>
    </div>
  </div>
)

/**
 * The mock's `DefaultToggle` (templates page.tsx:204). Turning one ON demotes the incumbent — done
 * by the BACKEND in the same statement for both template kinds, so this sends one PATCH and
 * refetches rather than looping over the others client-side as the mock does. A client that dies
 * halfway through that loop would leave two defaults and no way to tell which one publish picks.
 *
 * Offered on every row, built-in ones included: the choice is the tenant's, stored against the
 * tenant and never written to the template, so the ownership gates on Edit and Delete do not
 * apply to it.
 */
const DefaultToggle: React.FC<{
  checked: boolean
  busy?: boolean
  onChange: (v: boolean) => void
}> = ({ checked, busy, onChange }) => (
  <div className="flex shrink-0 flex-col items-center gap-1 pr-1">
    <span className="text-[11px] font-medium uppercase tracking-wide text-theme-text-tertiary">
      Default
    </span>
    <Toggle checked={checked} onChange={onChange} disabled={busy} label="Tenant default" />
  </div>
)

const Badge: React.FC<{ children: React.ReactNode; tone?: IntentTone }> = ({ children, tone }) => {
  // Intent carries meaning, so it carries colour: a Required row and an Uninstall row must not
  // read alike in a list someone is scanning.
  const toned: Record<string, string> = {
    green: 'border-emerald-500/40 text-emerald-500',
    blue: 'border-sky-500/40 text-sky-500',
    red: 'border-red-500/40 text-red-500',
    violet: 'border-violet-500/40 text-violet-500',
  }
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
        tone ? toned[tone] : 'border-theme-border-primary text-theme-text-tertiary'
      }`}
    >
      {children}
    </span>
  )
}


/** The mock keys a row's icon to what the template does when the app is running
 *  (its page.tsx:44-49), so a silent template and a prompting one are told apart at a glance. */
const uxRowIcon = (t: DeploymentTemplate): React.ReactNode => {
  const mode = (t.config_json as any)?.closeApps?.mode
  const cls = 'h-[18px] w-[18px]'
  if (mode === 'perform') return <BoltIcon className={cls} />
  if (mode === 'autoClose') return <XCircleIcon className={cls} />
  if (mode === 'skip') return <ClockIcon className={cls} />
  return <BellAlertIcon className={cls} />
}

/**
 * Whether the API will refuse an edit or a delete on this row.
 *
 * Mirrors the guards in api/templates.py and api/intune_templates.py exactly, because the row's
 * buttons are a claim about what the API will accept. Both refuse a SAP-global template to
 * anyone but a super admin; the Intune API additionally refuses any row flagged read-only, which
 * is how a vendor baseline stays fork-to-edit even inside the tenant that can see it.
 *
 * Getting this loose shows a button that 403s. Getting it tight hides an action the admin is
 * entitled to, which is the same defect and quieter — the Intune tab had the first, the User
 * Experience tab the second.
 */
const uxLocked = (t: DeploymentTemplate, superAdmin: boolean): boolean =>
  t.scope === 'global' && !superAdmin

const intuneLocked = (t: IntuneDeploymentTemplate, superAdmin: boolean): boolean =>
  !!t.read_only || (t.scope === 'global' && !superAdmin)

const Templates: React.FC = () => {
  const [tab, setTab] = useState<Tab>('ux')
  const qc = useQueryClient()
  // Both APIs refuse an edit or a delete on a SAP-global template UNLESS the caller is a super
  // admin, so the row's buttons have to know. Hiding them from a super admin is the same defect
  // as showing them to everyone else, only quieter: the action is possible and looks impossible.
  const superAdmin = useAuthStore((s) => s.isSuperAdmin)()
  const [uxEditing, setUxEditing] = useState<DeploymentTemplate | null>(null)
  const [uxCreating, setUxCreating] = useState(false)
  const [intuneEditing, setIntuneEditing] = useState<IntuneDeploymentTemplate | null>(null)
  const [intuneCreating, setIntuneCreating] = useState(false)
  // The wizard now occupies the content area rather than floating over it, so the page has
  // two mutually exclusive modes: list or wizard. One derived flag keeps the breadcrumb,
  // the list guard and both wizards from disagreeing about which mode is active.
  // ...and it also decides the width: a LIST wants a reading column, a FORM wants the page. Both
  // wizards get the full content area, so this needs no per-step reporting from the wizard.
  // Which row is mid-PATCH, so its switch can be disabled rather than flickering back.
  const [settingDefault, setSettingDefault] = useState<string | null>(null)
  // The row awaiting a delete confirmation. Held as the whole row so the prompt can name it and
  // say what is lost, rather than asking "are you sure" about nothing in particular.
  const [toDelete, setToDelete] = useState<
    { kind: 'ux' | 'intune'; id: string; name: string; usedBy?: TemplateUsage } | null
  >(null)
  const [rowError, setRowError] = useState<string | null>(null)
  // Shown after an edit to a template that deployments use: they keep the settings they were
  // published with, so the admin learns which ones the edit has not reached.
  const [savedNote, setSavedNote] = useState<{ name: string; usedBy: TemplateUsage } | null>(null)
  const noteSaved = (t: { name: string; used_by?: TemplateUsage } | null) => {
    setSavedNote(t?.used_by && t.used_by.count > 0 ? { name: t.name, usedBy: t.used_by } : null)
  }

  const invalidate = (kind: 'ux' | 'intune') =>
    qc.invalidateQueries({
      queryKey: [kind === 'ux' ? 'deployment-templates' : 'intune-templates'],
    })

  const duplicate = async (kind: 'ux' | 'intune', id: string) => {
    setRowError(null)
    try {
      if (kind === 'ux') await duplicateDeploymentTemplate(id)
      else await duplicateIntuneTemplate(id)
      await invalidate(kind)
    } catch (e: any) {
      setRowError(errorText(e, 'Could not duplicate the template.'))
    }
  }

  const confirmDelete = async () => {
    if (!toDelete) return
    setRowError(null)
    try {
      if (toDelete.kind === 'ux') await deleteDeploymentTemplate(toDelete.id)
      else await deleteIntuneTemplate(toDelete.id)
      await invalidate(toDelete.kind)
    } catch (e: any) {
      setRowError(errorText(e, 'Could not delete the template.'))
    }
  }
  const makeDefault = async (kind: 'ux' | 'intune', id: string, next: boolean) => {
    setSettingDefault(id)
    setRowError(null)
    try {
      if (kind === 'ux') await updateDeploymentTemplate(id, { is_default: next })
      else await updateIntuneTemplate(id, { is_default: next })
      qc.invalidateQueries({
        queryKey: [kind === 'ux' ? 'deployment-templates' : 'intune-templates'],
      })
    } catch (e: any) {
      // Without this the toggle just snapped back, and a refused or failed request left the
      // admin with a control that appeared to do nothing.
      setRowError(
        errorText(e, 'Could not change the default template.'))
    } finally {
      setSettingDefault(null)
    }
  }

  const wizardOpen = uxCreating || !!uxEditing || intuneCreating || !!intuneEditing
  const closeWizards = () => {
    setUxCreating(false)
    setUxEditing(null)
    setIntuneCreating(false)
    setIntuneEditing(null)
  }

  // Gap-audit item 1: the Intune wizard was rendered with NO groups prop, so its "existing
  // group" picker was always empty and generated groups were the only usable option.
  const groupsQ = useIntuneQuery({ queryKey: intuneKeys.adGroups(), queryFn: () => listAzureADGroups(), retry: false })
  const filtersQ = useIntuneQuery({ queryKey: intuneKeys.assignmentFilters(), queryFn: listAssignmentFilters, retry: false })
  // The Category chips are the tenant's own Intune categories, not a list we keep: Intune has no
  // fixed vocabulary, so a hardcoded one offers names the tenant does not have.
  const categoriesQ = useIntuneQuery({ queryKey: intuneKeys.appCategories(), queryFn: listIntuneAppCategories, retry: false })
  const ux = useQuery({ queryKey: ['deployment-templates'], queryFn: listDeploymentTemplates })
  const intune = useQuery({ queryKey: ['intune-templates'], queryFn: listIntuneTemplates })

  const uxTemplates = ux.data?.templates ?? []
  const intuneTemplates = intune.data?.templates ?? []

  return (
    <PageShell size={wizardOpen ? 'full' : 'wide'}>
      <PageHeader
        title="Templates"
        subtitle={
          <>
            Reusable settings applied when you deploy an app. Editing a template never changes an
            app you already published — the settings are copied onto the deployment at publish.
          </>
        }
      />

      {/* F-10b: the mock leads with an explainer and we had none, though the primitive existed.
          Collapsed by default, so it costs nothing to anyone who already knows. */}
      {!wizardOpen && (
        <HowItWorks title="How templates work">
          <p>
            A <strong>User Experience</strong> template decides what the person at the device sees:
            whether a dialog appears, how long they can postpone, and what the removal looks like.
            It is applied to the install command when you publish.
          </p>
          <p>
            An <strong>Intune Deployment</strong> template decides who gets the app and how it is
            described — assignments, the generated group names, the Intune display name, and the
            update policy.
          </p>
          <p>
            Deploying picks one of each. Editing a template afterwards never changes an app you have
            already published: the settings are copied onto the deployment at publish time, so
            history stays readable.
          </p>
          <p className="text-theme-text-tertiary">
            The <strong>Default</strong> switch marks the template a deployment starts from. Only
            one per kind can hold it — turning it on releases the previous one. A built-in
            template can be chosen too; choosing it does not change it.
          </p>
        </HowItWorks>
      )}

      {/* Tabs switch between the two LISTS, so they have nothing to do while a wizard is open —
          and offering them there would be a way to walk out of a half-filled form with no warning.
          The Breadcrumb below is the way back. The whole wrapper goes, not just the Tabs, or its
          margin would leave a gap where the tabs used to be. */}
      {!wizardOpen && (
        <div className="mb-5">
          <Tabs
            tabs={[
              { key: 'ux', label: 'User Experience', count: uxTemplates.length },
              { key: 'intune', label: 'Intune Deployment', count: intuneTemplates.length },
            ]}
            active={tab}
            onChange={(k) => setTab(k as Tab)}
          />
        </div>
      )}

      {!wizardOpen && (tab === 'ux' ? (
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-theme-text-primary mb-1">User Experience</h3>
            <button
              type="button"
              onClick={() => setUxCreating(true)}
              className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary"
            >
              New template
            </button>
          </div>
          <p className="text-xs text-theme-text-tertiary mb-4">
            What the end user sees on the device: the dialog, deferrals and timers, and the
            separate experience for uninstall. Applied to the install command at publish.
          </p>
          {ux.isLoading && <p className="text-theme-text-tertiary py-4">Loading…</p>}
          {!ux.isLoading && uxTemplates.length === 0 && (
            <EmptyState
              icon={<ComputerDesktopIcon className="h-7 w-7" />}
              title="No templates yet"
              hint="Create one to define what the end user sees."
            />
          )}
          <div className="space-y-2">
            {uxTemplates.map((t) => (
              <Row
                key={t.id}
                name={t.name}
                description={t.description}
                summary={uxSummary(t)}
                icon={uxRowIcon(t)}
                usedBy={t.used_by}
                /* A vendor-shipped template is Duplicate-only for a tenant admin: the API
                   answers 403 to an edit or a delete on one (api/templates.py), so offering
                   either would be a lie. A super admin owns those templates and may edit them,
                   which is the same condition the API checks. */
                onEdit={uxLocked(t, superAdmin) ? undefined : () => setUxEditing(t)}
                onDuplicate={() => duplicate('ux', t.id)}
                onDelete={
                  uxLocked(t, superAdmin)
                    ? undefined
                    : () => setToDelete({ kind: 'ux', id: t.id, name: t.name, usedBy: t.used_by })
                }
                defaultToggle={
                  <DefaultToggle
                    checked={!!t.is_default}
                    busy={settingDefault === t.id}
                    onChange={(v) => makeDefault('ux', t.id, v)}
                  />
                }
                badges={
                  <>
                    {t.scope === 'global' && <Badge>Built-in</Badge>}

                  </>
                }
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-theme-text-primary mb-1">Intune Deployment</h3>
            <button
              type="button"
              onClick={() => setIntuneCreating(true)}
              className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary"
            >
              New template
            </button>
          </div>
          <p className="text-xs text-theme-text-tertiary mb-4">
            Who gets the app and how: intent, target groups, notifications, the generated object
            names, and what happens when a new version is published.
          </p>
          {intune.isLoading && <p className="text-theme-text-tertiary py-4">Loading…</p>}
          {intune.isError && (
            <p className="text-red-400 py-4 text-sm">
              Could not load Intune deployment templates.
            </p>
          )}
          {!intune.isLoading && !intune.isError && intuneTemplates.length === 0 && (
            <EmptyState
              icon={<CloudArrowUpIcon className="h-7 w-7" />}
              title="No templates yet"
              hint="Create one to reuse targeting across apps."
            />
          )}
          <div className="space-y-2">
            {intuneTemplates.map((t) => (
              <Row
                key={t.id}
                name={t.name}
                /* A template saved without a description stores its summary as one; showing it
                   above the summary would print the same line twice. */
                description={
                  t.description?.trim() === intuneSummary(t, { omitPattern: true })
                    ? null
                    : t.description
                }
                summary={intuneSummary(t)}
                icon={<CloudArrowUpIcon className="h-[18px] w-[18px]" />}
                usedBy={t.used_by}
                chips={intuneAssignmentChips(t).map((c) => (
                  <Badge key={c.key} tone={c.tone}>{c.label}</Badge>
                ))}
                /* No Edit on a locked row: the API answers 403, so offering the button would
                   only teach an admin that the portal lies to them. Duplicate is what such a row
                   DOES offer — without it the row had no action at all, which is the advice the
                   API itself gives ("duplicate it and edit the copy"). */
                onEdit={intuneLocked(t, superAdmin) ? undefined : () => setIntuneEditing(t)}
                onDuplicate={() => duplicate('intune', t.id)}
                onDelete={
                  intuneLocked(t, superAdmin)
                    ? undefined
                    : () => setToDelete({ kind: 'intune', id: t.id, name: t.name, usedBy: t.used_by })
                }
                defaultToggle={
                  <DefaultToggle
                    checked={!!t.is_default}
                    busy={settingDefault === t.id}
                    onChange={(v) => makeDefault('intune', t.id, v)}
                  />
                }
                badges={
                  <>
                    {t.scope === 'global' && <Badge>Built-in</Badge>}
                    {t.read_only && <Badge>Read-only</Badge>}
                    {!t.enabled && <Badge>Disabled</Badge>}
                  </>
                }
              />
            ))}
          </div>
        </div>
      ))}
      {wizardOpen && (
        <Breadcrumb
          trail={[
            { label: 'Templates', onClick: closeWizards },
            {
              label: uxCreating
                ? 'New user-experience template'
                : uxEditing
                  ? `Edit ${uxEditing.name}`
                  : intuneCreating
                    ? 'New Intune deployment template'
                    : `Edit ${intuneEditing?.name ?? ''}`,
            },
          ]}
        />
      )}
      {(uxEditing || intuneEditing) && (
        <UsedBy
          usage={(uxEditing ?? intuneEditing)?.used_by}
          className="mb-3 text-xs text-theme-text-tertiary"
        />
      )}
      {(uxCreating || uxEditing) && (
        <UxTemplateWizard
          template={uxEditing ?? undefined}
          onClose={() => {
            setUxCreating(false)
            setUxEditing(null)
          }}
          onSaved={() => {
            noteSaved(uxEditing)
            qc.invalidateQueries({ queryKey: ['deployment-templates'] })
          }}
        />
      )}
      {rowError && <p className="mt-3 text-xs text-red-400">{rowError}</p>}
      {!wizardOpen && savedNote && (
        <div className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/[0.06] px-4 py-2.5 text-xs text-theme-text-secondary">
          <p>
            Saved {savedNote.name}. These deployments keep their settings until you update them:{' '}
            {savedNote.usedBy.deployments.join(', ')}.
          </p>
        </div>
      )}

      {/* Names what is lost, not just "are you sure": a prompt with no consequence in it teaches
          people to click through. Deleting a template never touches apps already published —
          their settings were copied onto the deployment at publish. */}
      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title={`Delete ${toDelete?.name ?? 'template'}?`}
        confirmLabel="Delete template"
        message={
          <>
            <p>
              Deployments already published from this template keep their settings — those were
              copied onto the deployment when it was published.
            </p>
            {toDelete?.usedBy && toDelete.usedBy.count > 0 && (
              <>
                <p className="mt-2">Used by {deploymentCount(toDelete.usedBy.count)}:</p>
                <ul className="mt-1 list-disc pl-5">
                  {toDelete.usedBy.deployments.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              </>
            )}
            <p className="mt-2">
              New deployments will no longer be able to start from it. This cannot be undone.
            </p>
          </>
        }
      />

      {(intuneCreating || intuneEditing) && (
        <DeploymentTemplateWizard
          template={intuneEditing ?? undefined}
          onClose={() => {
            setIntuneCreating(false)
            setIntuneEditing(null)
          }}
          groups={(groupsQ.data?.groups ?? []).map(toAssignmentGroup)}
          filters={filtersQ.data?.filters ?? []}
          categories={(categoriesQ.data?.categories ?? []).map((c) => c.name)}
          categoriesAvailable={!categoriesQ.isError && (categoriesQ.data?.available ?? true)}
          categoriesLoading={categoriesQ.isLoading}
          onSaved={() => {
            noteSaved(intuneEditing)
            qc.invalidateQueries({ queryKey: ['intune-templates'] })
          }}
        />
      )}
    </PageShell>
  )
}

export default Templates
